import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  Pagination,
  TablePagination,
  TableRow,
  Paper,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Autocomplete,
  TextField,
  Button,
} from '@mui/material';
import { Person } from '@mui/icons-material';
import NotesPanel from '../common/NotesPanel';
import apiClient from '../../api/client';
import { Line } from 'react-chartjs-2';

interface Employee {
  employee_id: string;
  employee_name: string;
}

interface MonthData {
  month: string;
  days_allocated: number;
  available_days: number;
  allocation_percentage: number;
  salary_inr: number;
  salary_cost_usd: number;
}

interface Project {
  project_name: string;
  employee_id: string;
  employee_name: string;
  role: string;
  status: string;
  months: Record<string, MonthData>;
  total_days: number;
  total_cost: number;
  customer_name?: string;
}

interface EmployeeAllocations {
  employee_id: string;
  employee_name: string;
  period: {
    start_date: string;
    end_date: string;
    months: string[];
  };
  projects: Project[];
  total_projects: number;
  total_days: number;
  total_cost: number;
}

interface AllocationsTabProps {
  startDate: string | null;
  endDate: string | null;
  /** When true, use /api/v1/allocations with -new endpoints (employee-allocations-new, all-employee-allocations-new) */
  useNewAllocationApis?: boolean;
}

interface CategoryEmployeeData {
  employee_id: string;
  employee_name: string;
  role: string;
  employee_status: string;
  months: Record<string, { days: number; percentage: number; available_days: number; cost: number }>;
  total_days: number;
  total_available_days: number;
  total_percentage: number;
  total_cost: number;
}

// Types for the API responses
interface OverviewData {
  monthlyBreakdown: Array<{
    month: string;
    category: string;
    percentage: number;
  }>;
  summaryMetrics: {
    totalEmployees: number;
    totalProjects: number;
    totalAllocations: number;
    billedPercentage: number;
    unbilledPercentage: number;
    benchPercentage: number;
    unallocatedPercentage: number;
  };
}

const AllocationsTab = ({ startDate, endDate, useNewAllocationApis = false }: AllocationsTabProps) => {
  const apiBase = useNewAllocationApis ? '/api/v1/allocations' : '/api/v1/delivery';
  const listPath = useNewAllocationApis ? 'employee-allocations-new' : 'employee-allocations';
  const allPath = useNewAllocationApis ? 'all-employee-allocations-new' : 'all-employee-allocations';

  // Normalize to YYYY-MM-DD for API (handles YYYY-MM or full ISO)
  const toApiDate = (d: string | null): string => {
    if (!d) return '';
    const trimmed = d.trim();
    if (/^\d{4}-\d{2}$/.test(trimmed)) return `${trimmed}-01`;
    if (trimmed.length >= 10) return trimmed.slice(0, 10);
    return trimmed;
  };
  const [selectedEmployee, setSelectedEmployee] = useState<string>('');
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [allocations, setAllocations] = useState<EmployeeAllocations | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('Billed');
  const [categoryEmployees, setCategoryEmployees] = useState<CategoryEmployeeData[]>([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryDataLoaded, setCategoryDataLoaded] = useState(false);
  const [sortBy, setSortBy] = useState<'name' | 'percentage'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [viewType, setViewType] = useState<'days' | 'cost'>('days');
  const [totalCostAllEmployees, setTotalCostAllEmployees] = useState<number>(0);

  // Pagination for Employee Allocation Summary (new endpoint)
  const [categoryPage, setCategoryPage] = useState<number>(0); // 0-based for MUI
  const [categoryPageSize, setCategoryPageSize] = useState<number>(5);
  const [categoryTotal, setCategoryTotal] = useState<number>(-1); // -1 => unknown (MUI convention)
  const [categoryTotalPages, setCategoryTotalPages] = useState<number>(0);
  const [categoryPeriodMonths, setCategoryPeriodMonths] = useState<string[]>([]);
  const [data, setData] = useState<OverviewData | null>(null);


    // Helper function to generate month labels for charts based on date range
  const getChartMonthLabels = () => {
    if (!startDate || !endDate) return ['Apr-25', 'May-25', 'Jun-25', 'Jul-25'];
    
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const labels = [];
    
    const startYear = parseInt(startDate.split('-')[0]);
    const startMonth = parseInt(startDate.split('-')[1]);
    const endYear = parseInt(endDate.split('-')[0]);
    const endMonth = parseInt(endDate.split('-')[1]);
    
    let currentYear = startYear;
    let currentMonth = startMonth;
    
    while (currentYear < endYear || (currentYear === endYear && currentMonth <= endMonth)) {
      const monthName = monthNames[currentMonth - 1];
      const yearShort = currentYear.toString().slice(-2);
      labels.push(`${monthName}-${yearShort}`);
      
      currentMonth++;
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear++;
      }
    }
    
    return labels;
  };
  

  // Fetch employees for the selected period
  const fetchEmployees = useCallback(async () => {
    if (!startDate || !endDate) return;

    try {
      setLoading(true);
      setError(null);
      
      const startDateFormatted = toApiDate(startDate);
      const endDateFormatted = toApiDate(endDate);
      
      const response = await apiClient.get(`${apiBase}/${listPath}`, {
        params: {
          start_date: startDateFormatted,
          end_date: endDateFormatted,
        },
      });
      
      setEmployees(response.data.employees || []);

      // Build query parameters for date filtering
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const queryString = params.toString();
      const apiSuffix = queryString ? `?${queryString}` : '';
      // Fetch overview data from the real endpoint
      const overviewResponse = await apiClient.get(`/api/v1/delivery/overview-real${apiSuffix}`);
      setData(overviewResponse.data);

    } catch (err: any) {
      console.error('Error fetching employees:', err);
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail || err.message || 'Failed to load employees. Please try again.');
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, apiBase, listPath]);

  // Fetch allocations for selected employee
  const fetchEmployeeAllocations = useCallback(async () => {
    if (!selectedEmployee || !startDate || !endDate) {
      setAllocations(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const startDateFormatted = toApiDate(startDate);
      const endDateFormatted = toApiDate(endDate);
      
      const response = await apiClient.get(`${apiBase}/${listPath}`, {
        params: {
          start_date: startDateFormatted,
          end_date: endDateFormatted,
          employee_id: selectedEmployee,
        },
      });
      
      setAllocations(response.data);
    } catch (err: any) {
      console.error('Error fetching employee allocations:', err);
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (detail || 'Failed to load employee allocations. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [selectedEmployee, startDate, endDate, apiBase, listPath]);

  // Fetch all employees' allocations for category filtering using new optimized endpoint
  const fetchCategoryEmployees = useCallback(async () => {
    if (!startDate || !endDate) {
      setCategoryEmployees([]);
      setCategoryDataLoaded(false);
      setCategoryTotal(-1);
      setCategoryTotalPages(0);
      setCategoryPeriodMonths([]);
      return;
    }

    try {
      setCategoryLoading(true);
      const startDateFormatted = toApiDate(startDate);
      const endDateFormatted = toApiDate(endDate);
      
      const response = await apiClient.get(`${apiBase}/${allPath}`, {
        params: {
          start_date: startDateFormatted,
          end_date: endDateFormatted,
          ...(useNewAllocationApis
            ? { page: categoryPage + 1, page_size: categoryPageSize }
            : {}),
        },
      });
      
      const payload = response.data || {};
      const allAllocations = payload.items || payload.employees || [];
      const periodMonths: string[] = Array.isArray(payload.period?.months) ? payload.period.months : [];

      setCategoryTotal(typeof payload.total === 'number' ? payload.total : -1);
      setCategoryTotalPages(
        typeof payload.total_pages === 'number'
          ? payload.total_pages
          : typeof payload.total === 'number'
            ? Math.ceil(payload.total / categoryPageSize)
            : 0
      );
      setCategoryPeriodMonths(periodMonths);
      
      // Mark as loaded
      setCategoryDataLoaded(true);
      
      // Process and categorize employees
      const UNBILLED_PROJECTS = [
        'Delivery Overhead', 
        'Internal Product & POCs', 
        'Sales & Pre-Sales Support', 
        'Focaloid Website & Marketing'
      ];
      
      const BENCH_PROJECTS = [
        'Unallocated Delivery Cost', 
        'Internal Training'
      ];
      
      const categorizeProject = (projectName: string): string => {
        if (UNBILLED_PROJECTS.includes(projectName)) {
          return 'Unbilled';
        }
        if (BENCH_PROJECTS.includes(projectName) || projectName.toLowerCase().includes('bench') || projectName.toLowerCase().includes('unallocated')) {
          return 'Bench';
        }
        return 'Billed';
      };
      
      // First, calculate total cost across ALL employees (all categories) for percentage calculations
      let totalCostAllEmployees = 0;
      allAllocations.forEach((employeeData: any) => {
        if (!employeeData || !employeeData.projects) return;
        employeeData.projects.forEach((project: any) => {
          Object.entries(project.months || {}).forEach(([month, monthData]: [string, any]) => {
            totalCostAllEmployees += monthData.salary_inr || 0;
          });
        });
      });
      
      const processedEmployees: Record<string, CategoryEmployeeData> = {};
      
      allAllocations.forEach((employeeData: any) => {
        if (!employeeData || !employeeData.projects) return;
        
        const employeeId = employeeData.employee_id;
        const employeeName = employeeData.employee_name;
        const role = employeeData.role || '';
        
        // Determine employee category based on their projects
        const projectCategories = new Set<string>();
        employeeData.projects.forEach((project: any) => {
          const category = categorizeProject(project.project_name);
          projectCategories.add(category);
        });
        
        // Check if employee has any allocation in the selected category
        const hasCategoryAllocation = Array.from(projectCategories).includes(categoryFilter);
        
        if (!hasCategoryAllocation && categoryFilter !== 'Unallocated') {
          return; // Skip employees without allocations in this category
        }
        
        // Initialize employee data if not exists
        if (!processedEmployees[employeeId]) {
          processedEmployees[employeeId] = {
            employee_id: employeeId,
            employee_name: employeeName,
            role: role,
            employee_status: employeeData?.employee_status || '',
            months: {},
            total_days: 0,
            total_available_days: 0,
            total_percentage: 0,
            total_cost: 0,
          };
        }
        
        // Process projects in the selected category
        employeeData.projects.forEach((project: any) => {
          const projectCategory = categorizeProject(project.project_name);
          
          // Only process if this project matches the selected category
          if (categoryFilter === 'Unallocated') {
            // For unallocated, we need to calculate from available days - allocated days
            // This will be handled separately
            return;
          }
          
          if (projectCategory !== categoryFilter) {
            return;
          }
          
          Object.entries(project.months || {}).forEach(([month, monthData]: [string, any]) => {
            if (!processedEmployees[employeeId].months[month]) {
              processedEmployees[employeeId].months[month] = {
                days: 0,
                percentage: 0,
                available_days: monthData.available_days,
                cost: 0,
              };
            }
            
            processedEmployees[employeeId].months[month].days += monthData.days_allocated;
            processedEmployees[employeeId].months[month].cost += monthData.salary_inr || 0;
            processedEmployees[employeeId].months[month].available_days = Math.max(
              processedEmployees[employeeId].months[month].available_days,
              monthData.available_days
            );
          });
        });
      });
      
      // Handle Unallocated category separately
      if (categoryFilter === 'Unallocated') {
        allAllocations.forEach((employeeData: any) => {
          if (!employeeData || !employeeData.projects) return;
          
          const employeeId = employeeData.employee_id;
          const employeeName = employeeData.employee_name;
          const role = employeeData.role || '';
          
          // Get all months and calculate unallocated days
          const monthTotals: Record<string, { allocated: number; available: number }> = {};
          
          employeeData.projects.forEach((project: any) => {
            Object.entries(project.months || {}).forEach(([month, monthData]: [string, any]) => {
              if (!monthTotals[month]) {
                monthTotals[month] = { allocated: 0, available: 0 };
              }
              monthTotals[month].allocated += monthData.days_allocated;
              monthTotals[month].available = Math.max(
                monthTotals[month].available,
                monthData.available_days
              );
            });
          });
          
          // Also check employee's months data for available days
          if (employeeData.months) {
            Object.entries(employeeData.months).forEach(([month, monthInfo]: [string, any]) => {
              if (!monthTotals[month]) {
                monthTotals[month] = { allocated: 0, available: 0 };
              }
              monthTotals[month].available = Math.max(
                monthTotals[month].available,
                monthInfo.available_days || 0
              );
            });
          }
          
          // Calculate unallocated days for each month
          Object.entries(monthTotals).forEach(([month, totals]) => {
            const unallocatedDays = Math.max(0, totals.available - totals.allocated);
            
            if (unallocatedDays > 0) {
              if (!processedEmployees[employeeId]) {
                processedEmployees[employeeId] = {
                  employee_id: employeeId,
                  employee_name: employeeName,
                  role: role,
                  employee_status: employeeData?.employee_status || '',
                  months: {},
                  total_days: 0,
                  total_available_days: 0,
                  total_percentage: 0,
                  total_cost: 0,
                };
              }
              
              processedEmployees[employeeId].months[month] = {
                days: unallocatedDays,
                percentage: totals.available > 0 ? (unallocatedDays / totals.available) * 100 : 0,
                available_days: totals.available,
                cost: 0, // Unallocated has no cost
              };
            }
          });
        });
      }
      
      // Calculate totals for each employee (before filling missing months)
      // Use employee-specific available days so % is correct (e.g. exited employee with Apr–Jul only gets denominator for those months, not full period)
      Object.values(processedEmployees).forEach((emp) => {
        let totalDays = 0;
        let totalCost = 0;
        let totalAvailableDaysEmp = 0;

        Object.values(emp.months).forEach((monthData) => {
          totalDays += monthData.days;
          totalCost += monthData.cost;
          totalAvailableDaysEmp += monthData.available_days || 0;
        });

        emp.total_days = totalDays;
        emp.total_cost = totalCost;
        emp.total_available_days = totalAvailableDaysEmp;
        emp.total_percentage = totalAvailableDaysEmp > 0 ? (totalDays / totalAvailableDaysEmp) * 100 : 0;
      });
      
      // Store total cost for all employees (used for cost percentage calculations)
      // This is calculated above from all allocations before filtering
      
      // Get all unique months from all employees and sort chronologically
      const allMonthsSet = new Set<string>();
      Object.values(processedEmployees).forEach((emp) => {
        Object.keys(emp.months).forEach((month) => allMonthsSet.add(month));
      });
      
      // Sort months chronologically
      const sortMonths = (months: string[]): string[] => {
        return months.sort((a, b) => {
          // Convert "Apr-25" format to sortable date
          const parseMonth = (m: string) => {
            const [monthStr, yearStr] = m.split('-');
            const monthMap: Record<string, number> = {
              'Jan': 1, 'Feb': 2, 'Mar': 3, 'Apr': 4, 'May': 5, 'Jun': 6,
              'Jul': 7, 'Aug': 8, 'Sep': 9, 'Oct': 10, 'Nov': 11, 'Dec': 12
            };
            const year = parseInt('20' + yearStr);
            const month = monthMap[monthStr] || 0;
            return new Date(year, month - 1, 1).getTime();
          };
          return parseMonth(a) - parseMonth(b);
        });
      };
      
      const sortedMonths = sortMonths(periodMonths.length > 0 ? [...periodMonths] : Array.from(allMonthsSet));
      
      // Ensure all employees have entries for all months (fill missing with 0)
      Object.values(processedEmployees).forEach((emp) => {
        sortedMonths.forEach((month) => {
          if (!emp.months[month]) {
            emp.months[month] = {
              days: 0,
              percentage: 0,
              available_days: 0,
              cost: 0,
            };
          }
        });
      });
      
      // Sort employees - will be sorted later based on user selection
      const sortedEmployees = Object.values(processedEmployees);
      
      // Store total cost for all employees (used for cost percentage calculations)
      setTotalCostAllEmployees(totalCostAllEmployees);
      setCategoryEmployees(sortedEmployees);
    } catch (err) {
      console.error('Error fetching category employees:', err);
      setCategoryEmployees([]);
    } finally {
      setCategoryLoading(false);
    }
  }, [
    startDate,
    endDate,
    categoryFilter,
    apiBase,
    allPath,
    useNewAllocationApis,
    categoryPage,
    categoryPageSize,
  ]);



  // Fetch employees when date range changes
  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);
  
  // Auto-load category data when date range is available
  useEffect(() => {
    if (startDate && endDate) {
      fetchCategoryEmployees();
    } else {
      setCategoryDataLoaded(false);
      setCategoryEmployees([]);
    }
  }, [startDate, endDate, fetchCategoryEmployees]);

  // Reset to first page when date range changes
  useEffect(() => {
    setCategoryPage(0);
  }, [startDate, endDate]);

  // Fetch allocations when employee changes
  useEffect(() => {
    fetchEmployeeAllocations();

  }, [fetchEmployeeAllocations]);

  // Reset selected employee when date range changes
  useEffect(() => {
    setSelectedEmployee('');
    setAllocations(null);
  }, [startDate, endDate]);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'assigned':
        return 'primary';
      case 'billed':
        return 'success';
      case 'bench':
        return 'warning';
      case 'unbilled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getEmployeeStatusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
    if (!status || status.trim() === '') return 'default';
    const s = status.toLowerCase();
    if (s === 'active' || s === 'employed' || s === 'full-time' || s === 'part-time') return 'success';
    if (s === 'inactive' || s === 'terminated' || s === 'resigned' || s === 'left') return 'error';
    if (s === 'on leave' || s === 'bench' || s === 'contract' || s === 'probation') return 'warning';
    return 'default';
  };

  return (
    <Box>
      {/* Header */}
      {/* <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: '#1e40af' }}>
          Employee Allocations
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View employee project allocations and allocation details for selected periods
        </Typography>
      </Box> */}

      {/* Category Filter Table */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Employee Allocation Summary
          </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Category</InputLabel>
              <Select
                  value={categoryFilter}
                  label="Category"
                  onChange={(e) => {
                    setCategoryPage(0);
                    setCategoryFilter(e.target.value);
                  }}
                  disabled={categoryLoading || !startDate || !endDate}
                >
                  <MenuItem value="Billed">Billed</MenuItem>
                  <MenuItem value="Unbilled">Unbilled</MenuItem>
                  <MenuItem value="Bench">Bench</MenuItem>
                  <MenuItem value="Unallocated">Unallocated</MenuItem>
              </Select>
            </FormControl>
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>View</InputLabel>
                <Select
                  value={viewType}
                  label="View"
                  onChange={(e) => setViewType(e.target.value as 'days' | 'cost')}
                  disabled={categoryLoading || categoryEmployees.length === 0}
                >
                  <MenuItem value="days">Days</MenuItem>
                  <MenuItem value="cost">Cost</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value as 'name' | 'percentage')}
                  disabled={categoryLoading || categoryEmployees.length === 0}
                >
                  <MenuItem value="name">Name</MenuItem>
                  <MenuItem value="percentage">Total %</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Order</InputLabel>
                <Select
                  value={sortOrder}
                  label="Order"
                  onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                  disabled={categoryLoading || categoryEmployees.length === 0}
                >
                  <MenuItem value="asc">Ascending</MenuItem>
                  <MenuItem value="desc">Descending</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {categoryLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : categoryEmployees.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No employees found in the {categoryFilter} category for the selected period.
            </Typography>
          ) : (() => {
            // Sort months chronologically
            const sortMonths = (months: string[]): string[] => {
              return months.sort((a, b) => {
                const parseMonth = (m: string) => {
                  const [monthStr, yearStr] = m.split('-');
                  const monthMap: Record<string, number> = {
                    'Jan': 1, 'Feb': 2, 'Mar': 3, 'Apr': 4, 'May': 5, 'Jun': 6,
                    'Jul': 7, 'Aug': 8, 'Sep': 9, 'Oct': 10, 'Nov': 11, 'Dec': 12
                  };
                  const year = parseInt('20' + yearStr);
                  const month = monthMap[monthStr] || 0;
                  return new Date(year, month - 1, 1).getTime();
                };
                return parseMonth(a) - parseMonth(b);
              });
            };
            
            // Prefer API period months (stable across pages), else derive from first employee
            const months =
              categoryPeriodMonths.length > 0
                ? sortMonths([...categoryPeriodMonths])
                : categoryEmployees[0]
                  ? sortMonths(Object.keys(categoryEmployees[0].months))
                  : [];
            
            // Get total available days for period (same for all employees)
            const totalAvailableDaysForPeriod = categoryEmployees[0]?.total_available_days || 0;
            
            // Use total cost across ALL employees (all categories) for percentage calculations
            const totalCostForPeriod = totalCostAllEmployees;
            
            // Sort employees based on selected criteria
            const sortedEmployees = [...categoryEmployees].sort((a, b) => {
              let comparison = 0;
              if (sortBy === 'name') {
                comparison = a.employee_name.localeCompare(b.employee_name);
              } else if (sortBy === 'percentage') {
                comparison = a.total_percentage - b.total_percentage;
              }
              return sortOrder === 'asc' ? comparison : -comparison;
            });
            
            return (
              <Box>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}
                >
                  <Table size="small" sx={{ minWidth: 720 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                        Employee Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                        Role
                      </TableCell>

                      <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                        Status
                      </TableCell>
                      {months.map((month) => (
                        <TableCell key={month} align="center" sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                          {month}
                      </TableCell>
                      ))}
                      <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                        {viewType === 'days' ? 'Total Days' : 'Total Cost'}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                        Total %
                      </TableCell>
                    </TableRow>
                  </TableHead>



                  
                  <TableBody>
                    {sortedEmployees.map((employee) => (
                      <TableRow key={employee.employee_id} hover>
                        {/* Employee Name */}
                        <TableCell sx={{ fontWeight: 'medium' }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {employee.employee_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {employee.employee_id}
                            </Typography>
                          </Box>
                        </TableCell>
                        
                        {/* Role */}
                        <TableCell>
                          {employee.role || '-'}
                        </TableCell>
                    
                        {/* Status */}
                        <TableCell>
                          <Chip
                            label={employee.employee_status || '—'}
                            size="small"
                            color={getEmployeeStatusColor(employee.employee_status || '')}
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.75rem',
                            }}
                          />
                        </TableCell>

                        {months.map((month) => {
                          const monthData = employee.months[month];
                          if (viewType === 'days') {
                            // Calculate percentage based on available days for that specific month (same as employee detail table)
                            const monthPercentage = monthData && monthData.available_days > 0 && monthData.days > 0
                              ? (monthData.days / monthData.available_days) * 100
                              : 0;
                            return (
                              <TableCell key={month} align="center">
                                {monthData && monthData.days > 0 ? (
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                      {monthData.days.toFixed(1)} days
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {Math.round(monthPercentage)}%
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    -
                                  </Typography>
                                )}
                        </TableCell>
                            );
                          } else {
                            // Cost view
                            const monthCost = monthData?.cost || 0;
                            const monthCostPercentage = totalCostForPeriod > 0 && monthCost > 0
                              ? (monthCost / totalCostForPeriod) * 100
                              : 0;
                            return (
                              <TableCell key={month} align="center">
                                {monthCost > 0 ? (
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                      ₹{monthCost.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {Math.round(monthCostPercentage)}%
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    -
                                  </Typography>
                                )}
                        </TableCell>
                            );
                          }
                        })}
                        <TableCell align="center" sx={{ fontWeight: 'medium' }}>
                          {viewType === 'days' ? (
                            `${employee.total_days.toFixed(0)}/${employee.total_available_days.toFixed(0)}`
                          ) : (
                            `₹${employee.total_cost.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'medium' }}>
                          {viewType === 'days' ? (
                            Math.round(employee.total_percentage)
                          ) : (
                            totalCostForPeriod > 0 ? Math.round((employee.total_cost / totalCostForPeriod) * 100) : 0
                          )}%
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Total Row */}
                    {(() => {
                      const monthTotals: Record<string, { days: number; cost: number; available: number }> = {};
                      let grandTotalDays = 0;
                      let grandTotalCost = 0;

                      categoryEmployees.forEach((emp) => {
                        grandTotalDays += emp.total_days;
                        grandTotalCost += emp.total_cost;

                        Object.entries(emp.months).forEach(([month, monthData]) => {
                          if (!monthTotals[month]) {
                            monthTotals[month] = { days: 0, cost: 0, available: 0 };
                          }
                          monthTotals[month].days += monthData.days;
                          monthTotals[month].cost += monthData.cost;
                          monthTotals[month].available = Math.max(
                            monthTotals[month].available,
                            monthData.available_days
                          );
                        });
                      });

                      // Period total available days for grand total row (sum of calendar available per month in range)
                      const totalAvailableDaysForPeriod = Object.values(monthTotals).reduce((sum, t) => sum + t.available, 0);

                      // Calculate percentage based on view type
                      const grandTotalPercentage = viewType === 'days'
                        ? (totalAvailableDaysForPeriod > 0 ? (grandTotalDays / totalAvailableDaysForPeriod) * 100 : 0)
                        : (totalCostForPeriod > 0 ? (grandTotalCost / totalCostForPeriod) * 100 : 0);
                      
                      return (
                        <TableRow sx={{ backgroundColor: 'grey.50', '& td': { fontWeight: 'bold', borderTop: '2px solid', borderColor: 'divider' } }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>TOTAL</TableCell>
                          <TableCell></TableCell>
                          {months.map((month) => {
                            const total = monthTotals[month];
                            if (viewType === 'days') {
                              if (total && total.days > 0) {
                                // Calculate percentage based on available days for that specific month (same as employee detail table)
                                const monthPercentage = total.available > 0 
                                  ? (total.days / total.available) * 100 
                                  : 0;
                                return (
                                  <TableCell key={month} align="center" sx={{ fontWeight: 'bold' }}>
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        {total.days.toFixed(1)} days
              </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                        {Math.round(monthPercentage)}%
            </Typography>
                                    </Box>
                                  </TableCell>
                                );
                              }
                            } else {
                              // Cost view
                              if (total && total.cost > 0) {
                                const monthCostPercentage = totalCostForPeriod > 0 
                                  ? (total.cost / totalCostForPeriod) * 100 
                                  : 0;
                                return (
                                  <TableCell key={month} align="center" sx={{ fontWeight: 'bold' }}>
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        ₹{total.cost.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                        {Math.round(monthCostPercentage)}%
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                );
                              }
                            }
                            return (
                              <TableCell key={month} align="center" sx={{ fontWeight: 'bold' }}>
                                -
                              </TableCell>
                            );
                          })}
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                            {viewType === 'days' ? (
                              `${grandTotalDays.toFixed(0)}/${totalAvailableDaysForPeriod.toFixed(0)}`
                            ) : (
                              `₹${grandTotalCost.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                            {Math.round(grandTotalPercentage)}%
                          </TableCell>
                        </TableRow>
                      );
                    })()}
                  </TableBody>
                  </Table>
                </TableContainer>

                {useNewAllocationApis && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    <TablePagination
                      component="div"
                      count={categoryTotal}
                      page={categoryPage}
                      onPageChange={(_, newPage) => setCategoryPage(newPage)}
                      rowsPerPage={categoryPageSize}
                      onRowsPerPageChange={(e) => {
                        const next = parseInt(e.target.value, 10);
                        setCategoryPageSize(Number.isFinite(next) ? next : 5);
                        setCategoryPage(0);
                      }}
                      rowsPerPageOptions={[5, 10, 20, 50]}
                      ActionsComponent={() => null}
                      disabled={categoryLoading}
                    />

                    {categoryTotalPages > 1 && (
                      <Pagination
                        count={categoryTotalPages}
                        page={categoryPage + 1}
                        onChange={(_, pageNum) => setCategoryPage(pageNum - 1)}
                        showFirstButton
                        showLastButton
                        color="primary"
                        size="small"
                        disabled={categoryLoading}
                      />
                    )}
                  </Box>
                )}
              </Box>
            );
          })()}
        </CardContent>
      </Card>

      {/* Alocation chart */}

      <Card sx={{mb:3}}>
        <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Allocation Trends (Percentage)
                      </Typography>
                      <Box sx={{ height: 400 }}>
                        {data?.monthlyBreakdown && (
                          <Line
                            data={{
                              labels: getChartMonthLabels(),
                              datasets: [
                                {
                                  label: 'Billed %',
                                  data: getChartMonthLabels().map(month => 
                                    data.monthlyBreakdown.find((item: any) => item.month === month && item.category === 'billed')?.percentage || 0
                                  ),
                                  borderColor: '#0EA5E9',
                                  backgroundColor: 'rgba(34, 197, 94, 0.1)',
                                  borderWidth: 3,
                                  fill: false,
                                  tension: 0.4,
                                  pointBackgroundColor: '#0EA5E9',
                                  pointBorderColor: '#fff',
                                  pointBorderWidth: 2,
                                  pointRadius: 4,
                                  pointHoverRadius: 4,
                                },
                                {
                                  label: 'Unbilled %',
                                  data: getChartMonthLabels().map(month => 
                                    data.monthlyBreakdown.find((item: any) => item.month === month && item.category === 'unbilled')?.percentage || 0
                                  ),
                                  borderColor: '#12f5cfec',
                                  backgroundColor: 'rgba(245, 158, 11, 0.1)',
                                  borderWidth: 3,
                                  fill: false,
                                  tension: 0.4,
                                  pointBackgroundColor: '#12f5cfec',
                                  pointBorderColor: '#fff',
                                  pointBorderWidth: 2,
                                  pointRadius: 4,
                                  pointHoverRadius: 4,
                                },
                                {
                                  label: 'Bench %',
                                  data: getChartMonthLabels().map(month => 
                                    data.monthlyBreakdown.find((item: any) => item.month === month && item.category === 'bench')?.percentage || 0
                                  ),
                                  borderColor: '#EF4444',
                                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                  borderWidth: 3,
                                  fill: false,
                                  tension: 0.4,
                                  pointBackgroundColor: '#EF4444',
                                  pointBorderColor: '#fff',
                                  pointBorderWidth: 2,
                                  pointRadius: 4,
                                  pointHoverRadius: 4,
                                },
                              ],
                            }}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              animation: {
                                duration: 0,
                              },
                              hover: {
                                mode: 'nearest' as const,
                                intersect: false,
                              },
                              plugins: {
                                legend: {
                                  position: 'top' as const,
                                  labels: {
                                    usePointStyle: true,
                                    padding: 20,
                                  },
                                },
                                tooltip: {
                                  enabled: true,
                                  mode: 'index' as const,
                                  intersect: false,
                                  animation: false,
                                  callbacks: {
                                    label: function(context) {
                                      const value = context.parsed.y;
                                      return `${context.dataset.label}: ${value.toFixed(1)}%`;
                                    },
                                  },
                                },
                              },
                              scales: {
                                x: {
                                  display: true,
                                  title: {
                                    display: true,
                                    text: 'Month',
                                  },
                                  grid: {
                                    display: true,
                                    color: 'rgba(0, 0, 0, 0.1)',
                                  },
                                },
                                y: {
                                  display: true,
                                  title: {
                                    display: true,
                                    text: 'Percentage (%)',
                                  },
                                  min: 0,
                                  max: 100,
                                  grid: {
                                    display: true,
                                    color: 'rgba(0, 0, 0, 0.1)',
                                  },
                                  ticks: {
                                    callback: function(value) {
                                      return value + '%';
                                    },
                                  },
                                },
                              },
                              interaction: {
                                intersect: false,
                              },
                            }}
                          />
                        )}
                      </Box>
                    
        </CardContent>
      </Card>

      {/* Employee Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Select Employee
          </Typography>
          <Autocomplete
            options={employees}
            getOptionLabel={(option) => option.employee_name}
            value={employees.find(emp => emp.employee_id === selectedEmployee) || null}
            onChange={(event, newValue) => {
              setSelectedEmployee(newValue ? newValue.employee_id : '');
            }}
            disabled={loading || employees.length === 0}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search and select employee"
                placeholder="Type to search employees..."
                variant="outlined"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                      <Person fontSize="small" color="action" />
                    </Box>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.employee_id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Person fontSize="small" color="action" />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {option.employee_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {option.employee_id}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            filterOptions={(options, { inputValue }) => {
              if (!inputValue) return options;
              const filtered = options.filter((option) =>
                option.employee_name.toLowerCase().includes(inputValue.toLowerCase()) ||
                option.employee_id.toLowerCase().includes(inputValue.toLowerCase())
              );
              return filtered;
            }}
            noOptionsText="No employees found"
            sx={{ maxWidth: 500 }}
            isOptionEqualToValue={(option, value) => option.employee_id === value?.employee_id}
          />
          {employees.length === 0 && !loading && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No employees found for the selected period
            </Typography>
          )}
          {employees.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {employees.length} employees available for the selected period
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Employee Allocations */}
      {allocations && !loading && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Person sx={{ fontSize: 32, color: '#1e40af' }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {allocations.employee_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Employee ID: {allocations.employee_id}
                </Typography>
              </Box>
            </Box>

            {/* Allocation Status Summary */}
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
              Allocation Status Breakdown
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {(() => {
                // Calculate total available days and allocated days
                const monthAvailableDays: Record<string, number> = {};
                const monthAllocatedDays: Record<string, number> = {};
                const statusBreakdown: Record<string, { days: number, cost: number, projects: number }> = {};

                // Define project categories - same logic as Dashboard
                const UNBILLED_PROJECTS = [
                  'Delivery Overhead', 
                  'Internal Product & POCs', 
                  'Sales & Pre-Sales Support', 
                  'Focaloid Website & Marketing'
                ];
                
                const BENCH_PROJECTS = [
                  'Unallocated Delivery Cost', 
                  'Internal Training'
                ];

                // Process each project and month to get accurate totals
                allocations.projects.forEach(project => {
                  Object.entries(project.months).forEach(([month, monthData]) => {
                    // Store available days for each month (only once per month)
                    if (!monthAvailableDays[month]) {
                      monthAvailableDays[month] = monthData.available_days;
                    }
                    
                    // Aggregate allocated days per month (handle multiple allocations to same project)
                    if (!monthAllocatedDays[month]) {
                      monthAllocatedDays[month] = 0;
                    }
                    monthAllocatedDays[month] += monthData.days_allocated;
                    
                    // Categorize based on project name - same logic as Dashboard
                    let category;
                    if (UNBILLED_PROJECTS.includes(project.project_name)) {
                      category = 'unbilled';
                    } else if (BENCH_PROJECTS.includes(project.project_name)) {
                      category = 'bench';
                    } else {
                      category = 'billed';
                    }
                    
                    if (!statusBreakdown[category]) {
                      statusBreakdown[category] = { days: 0, cost: 0, projects: 0 };
                    }
                    statusBreakdown[category].days += monthData.days_allocated;
                    statusBreakdown[category].cost += monthData.salary_inr;
                    statusBreakdown[category].projects += 1;
                  });
                });

                // Calculate total available days (sum of unique months)
                const totalAvailableDays = Object.values(monthAvailableDays).reduce((sum, days) => sum + days, 0);
                
                // Calculate total allocated days (sum of all allocations per month)
                const totalAllocatedDays = Object.values(monthAllocatedDays).reduce((sum, days) => sum + days, 0);
                
                // Calculate unallocated days
                const unallocatedDays = totalAvailableDays - totalAllocatedDays;

                // Debug: Log status breakdown
                console.log('Status Breakdown:', statusBreakdown);
                console.log('Total Available Days:', totalAvailableDays);
                console.log('Total Allocated Days:', totalAllocatedDays);
                console.log('Unallocated Days:', unallocatedDays);

                const statusCards = [
                  { key: 'billed', label: 'Billed', color: 'success' as const, icon: '💰' },
                  { key: 'unbilled', label: 'Unbilled', color: 'error' as const, icon: '⏳' },
                  { key: 'bench', label: 'Bench', color: 'warning' as const, icon: '🪑' },
                  { key: 'unallocated', label: 'Unallocated', color: 'grey' as const, icon: '❓' },
                ];

                return statusCards.map(({ key, label, color, icon }) => {
                  let data, percentage;
                  
                  if (key === 'unallocated') {
                    data = { days: unallocatedDays, cost: 0, projects: 0 };
                    percentage = totalAvailableDays > 0 ? (unallocatedDays / totalAvailableDays) * 100 : 0;
                  } else {
                    data = statusBreakdown[key] || { days: 0, cost: 0, projects: 0 };
                    // Calculate percentage based on total available days (not allocated days)
                    percentage = totalAvailableDays > 0 ? (data.days / totalAvailableDays) * 100 : 0;
                  }
                  
                  return (
                    <Grid item xs={12} sm={6} md={3} key={key}>
                      <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: `${color}.light` }}>
                        <Typography variant="h4" sx={{ mb: 1 }}>
                          {icon}
                        </Typography>
                        <Typography variant="h6" color={`${color}.dark`}>
                          {Math.round(percentage)}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          {data.days.toFixed(1)} days
                          {key !== 'unallocated' && ` • ${data.projects} projects`}
                        </Typography>
                      </Paper>
                    </Grid>
                  );
                });
              })()}
            </Grid>

            {/* Projects Table */}
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Project Allocations
            </Typography>
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}
            >
              <Table size="small" sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                      Project Name
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                      Customer
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                      Role
                    </TableCell>
                    {allocations.period.months.map((month) => (
                      <TableCell key={month} align="center" sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                        {month}
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                      Total Days
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: 'grey.100' }}>
                      Total Cost (INR)
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allocations.projects.map((project, index) => (
                    <TableRow key={index} hover>
                      <TableCell sx={{ fontWeight: 'medium' }}>
                        {project.project_name}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'medium' }}>
                        {project.customer_name}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={project.status}
                          color={getStatusColor(project.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'medium' }}>
                        {project.role}
                      </TableCell>
                      {allocations.period.months.map((month) => {
                        const monthData = project.months[month];
                        return (
                          <TableCell key={month} align="center">
                            {monthData ? (
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  {monthData.days_allocated} days
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {Math.round(monthData.allocation_percentage)}%
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                -
                              </Typography>
                            )}
                          </TableCell>
                        );
                      })}
                      <TableCell align="center" sx={{ fontWeight: 'medium' }}>
                        {project.total_days.toFixed(1)}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'medium' }}>
                        ₹{project.total_cost.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Total Row */}
                  {(() => {
                    // Calculate totals per month
                    const monthTotals: Record<string, { days: number; availableDays: number }> = {};
                    
                    allocations.projects.forEach(project => {
                      Object.entries(project.months).forEach(([month, monthData]) => {
                        if (!monthTotals[month]) {
                          monthTotals[month] = {
                            days: 0,
                            availableDays: monthData.available_days
                          };
                        }
                        monthTotals[month].days += monthData.days_allocated;
                        // available_days should be the same for all projects in the same month
                        // but use the first one we encounter
                      });
                    });

                    // Calculate grand totals
                    const grandTotalDays = allocations.projects.reduce((sum, p) => sum + p.total_days, 0);
                    const grandTotalCost = allocations.projects.reduce((sum, p) => sum + p.total_cost, 0);

                    return (
                      <TableRow sx={{ backgroundColor: 'grey.50', '& td': { fontWeight: 'bold', borderTop: '2px solid', borderColor: 'divider' } }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>TOTAL</TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        {allocations.period.months.map((month) => {
                          const total = monthTotals[month];
                          if (total) {
                            const totalPercentage = total.availableDays > 0 
                              ? Math.round((total.days / total.availableDays) * 100)
                              : 0;
                            return (
                              <TableCell key={month} align="center" sx={{ fontWeight: 'bold' }}>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {total.days.toFixed(1)} days
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                    {totalPercentage}%
                                  </Typography>
                                </Box>
                              </TableCell>
                            );
                          }
                          return (
                            <TableCell key={month} align="center" sx={{ fontWeight: 'bold' }}>
                              -
                            </TableCell>
                          );
                        })}
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                          {grandTotalDays.toFixed(1)}
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                          ₹{grandTotalCost.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    );
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}


    </Box>
  );
};

export default AllocationsTab;
